import { parseGeographyPoint } from "@bednbody/logic";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export interface HubPortfolioSummary {
  bookings: number;
  cancelledBookings: number;
  deliveredValue: number;
  activeStaff: number;
}

export interface HubPoint {
  lat: number;
  lng: number;
}

const EMPTY_SUMMARY: HubPortfolioSummary = {
  bookings: 0,
  cancelledBookings: 0,
  deliveredValue: 0,
  activeStaff: 0,
};

function analyticsWindow(days: number) {
  const since = new Date();
  since.setDate(since.getDate() - (days - 1));
  since.setHours(0, 0, 0, 0);
  const until = new Date();
  until.setHours(0, 0, 0, 0);
  until.setDate(until.getDate() + 1);
  return { since, until };
}

/** Geography can arrive from PostgREST as GeoJSON, WKT, or EWKB hex depending
 * on the PostGIS adapter. The decode lives in `@bednbody/logic` because the
 * Scheduled availability Edge Function needs the same one — two copies of a
 * coordinate parser is how the two quietly disagree. Never invents a fallback
 * location when a hub has no usable coordinate. */
export const parseHubPoint = (geo: unknown): HubPoint | null => parseGeographyPoint(geo);

/** Full hub list for the management page. This keeps the geography needed by
 * the detail map but does not expose client information. */
export function useHubsAdmin() {
  return useQuery({
    queryKey: ["hubs-list", "admin"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("hubs")
        .select(
          "id, name, region, geo, is_pilot, bookable, service_radius_km, clock_in_radius_m, created_at",
        )
        .order("name");
      if (error) throw error;
      return data;
    },
    staleTime: 60_000,
  });
}

/** Compact last-30-day operating numbers for every hub card. */
export function useHubPortfolioSummary() {
  return useQuery({
    queryKey: ["hub-portfolio-summary", 30],
    queryFn: async (): Promise<Record<string, HubPortfolioSummary>> => {
      const { since, until } = analyticsWindow(30);
      const [bookingsResult, staffResult] = await Promise.all([
        supabase
          .from("bookings")
          .select("hub_id, status, gross_amount")
          .gte("scheduled_at", since.toISOString())
          .lt("scheduled_at", until.toISOString())
          .limit(5000),
        supabase.from("staff").select("hub_id, active"),
      ]);
      if (bookingsResult.error) throw bookingsResult.error;
      if (staffResult.error) throw staffResult.error;

      const summary: Record<string, HubPortfolioSummary> = {};
      const forHub = (hubId: string) => (summary[hubId] ??= { ...EMPTY_SUMMARY });

      for (const booking of bookingsResult.data ?? []) {
        const totals = forHub(booking.hub_id);
        if (booking.status === "cancelled") {
          totals.cancelledBookings += 1;
        } else {
          totals.bookings += 1;
          if (booking.status === "completed") totals.deliveredValue += booking.gross_amount;
        }
      }
      for (const staff of staffResult.data ?? []) {
        if (staff.hub_id && staff.active) forHub(staff.hub_id).activeStaff += 1;
      }
      return summary;
    },
    staleTime: 60_000,
  });
}

/** Dedicated hub workspace: last-30-day delivery activity, non-PII booking
 * references, and active team composition. */
export function useHubDetail(hubId: string | undefined) {
  return useQuery({
    queryKey: ["hub-detail", hubId, 30],
    enabled: Boolean(hubId),
    queryFn: async () => {
      if (!hubId) throw new Error("Hub id is required.");
      const { since, until } = analyticsWindow(30);
      const [hubResult, bookingsResult, staffResult] = await Promise.all([
        supabase
          .from("hubs")
          .select(
            "id, name, region, code, geo, is_pilot, bookable, service_radius_km, clock_in_radius_m, created_at",
          )
          .eq("id", hubId)
          .maybeSingle(),
        supabase
          .from("bookings")
          .select("id, ref, status, gross_amount, scheduled_at")
          .eq("hub_id", hubId)
          .gte("scheduled_at", since.toISOString())
          .lt("scheduled_at", until.toISOString())
          .order("scheduled_at", { ascending: false })
          .limit(5000),
        supabase.from("staff").select("id, role, active").eq("hub_id", hubId),
      ]);
      if (hubResult.error) throw hubResult.error;
      if (bookingsResult.error) throw bookingsResult.error;
      if (staffResult.error) throw staffResult.error;
      if (!hubResult.data) return null;

      const dayKey = (date: Date) =>
        `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
          date.getDate(),
        ).padStart(2, "0")}`;
      const daily = new Map<string, { bookings: number; cancelled: number; earnings: number }>();
      const summary = { ...EMPTY_SUMMARY };
      for (const booking of bookingsResult.data ?? []) {
        const key = dayKey(new Date(booking.scheduled_at));
        const day = daily.get(key) ?? { bookings: 0, cancelled: 0, earnings: 0 };
        if (booking.status === "cancelled") {
          summary.cancelledBookings += 1;
          day.cancelled += 1;
        } else {
          summary.bookings += 1;
          day.bookings += 1;
          if (booking.status === "completed") {
            summary.deliveredValue += booking.gross_amount;
            day.earnings += booking.gross_amount;
          }
        }
        daily.set(key, day);
      }
      for (const member of staffResult.data ?? []) {
        if (member.active) summary.activeStaff += 1;
      }

      const dailyActivity = Array.from({ length: 30 }, (_, offset) => {
        const date = new Date(since);
        date.setDate(since.getDate() + offset);
        const totals = daily.get(dayKey(date)) ?? { bookings: 0, cancelled: 0, earnings: 0 };
        return { date, ...totals };
      });
      const staffByRole = (staffResult.data ?? []).reduce<Record<string, number>>(
        (roles, member) => {
          if (member.active) roles[member.role] = (roles[member.role] ?? 0) + 1;
          return roles;
        },
        {},
      );

      return {
        hub: hubResult.data,
        summary,
        dailyActivity,
        staffByRole,
        recentBookings: (bookingsResult.data ?? []).slice(0, 6),
      };
    },
    staleTime: 60_000,
  });
}

/** Plain RLS write: `hubs_admin_write` (0003) grants insert to admin +
 * superadmin. `geo` travels as WKT (`POINT(lng lat)` - lng first) into
 * geography(Point,4326). `clock_in_radius_m` (0026) is required at creation
 * — no product-specified default exists, so the form has no fallback value
 * either (rules/never-invent-policy.md). */
export function useCreateHub() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      name: string;
      region: string;
      serviceRadiusKm: number;
      clockInRadiusM: number;
      isPilot: boolean;
      /**
       * `hubs.bookable` (0056) — may CLIENTS be served from this hub? Defaults
       * false in SQL and the form leaves it off, so a hub is invisible to
       * booking until someone deliberately turns it on. Distinct from
       * `isPilot`, which only records where the pilot runs and drives nothing.
       */
      bookable: boolean;
      lat: number | null;
      lng: number | null;
    }) => {
      const { data, error } = await supabase
        .from("hubs")
        .insert({
          name: input.name,
          region: input.region,
          service_radius_km: input.serviceRadiusKm,
          clock_in_radius_m: input.clockInRadiusM,
          is_pilot: input.isPilot,
          bookable: input.bookable,
          geo: input.lat !== null && input.lng !== null ? `POINT(${input.lng} ${input.lat})` : null,
        })
        .select("id, name")
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["hubs-list"] });
      queryClient.invalidateQueries({ queryKey: ["hub-portfolio-summary"] });
    },
  });
}

/** Plain RLS write, same grant as useCreateHub (`hubs_admin_write`, 0003) —
 * admin/superadmin already hold UPDATE on `hubs`. A small inline-edit
 * affordance rather than a full hub-edit page: this is the one field a hub
 * created before migration 0026 needs set before riders there can clock in
 * (no backfilled value exists — see 0026's own comment). */
export function useUpdateHubClockInRadius() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { hubId: string; clockInRadiusM: number }) => {
      const { error } = await supabase
        .from("hubs")
        .update({ clock_in_radius_m: input.clockInRadiusM })
        .eq("id", input.hubId);
      if (error) throw error;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["hubs-list"] });
      queryClient.invalidateQueries({ queryKey: ["hub-detail", variables.hubId] });
    },
  });
}

/** 0330: a hub's own service radius, after creation. Same grant as the
 * clock-in radius above. The engines use min(this, the Settings service area),
 * so raising it past that limit changes nothing until the limit moves too. */
export function useUpdateHubServiceRadius() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { hubId: string; serviceRadiusKm: number }) => {
      const { error } = await supabase
        .from("hubs")
        .update({ service_radius_km: input.serviceRadiusKm })
        .eq("id", input.hubId);
      if (error) throw error;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["hubs-list"] });
      queryClient.invalidateQueries({ queryKey: ["hub-detail", variables.hubId] });
    },
  });
}

/**
 * Move an EXISTING hub's pin — a relocation, or correcting one placed
 * imprecisely at creation.
 *
 * ⚠ THIS IS A LIVE REFERENCE POINT, NOT A SNAPSHOT. Nothing in the schema
 * freezes a hub's `geo` the way `trips.mileage_rate_snapshot` freezes a
 * rate (money-math.md) — distance-based reads (service radius, hub→client
 * km, dispatch ETA) all resolve from the CURRENT row. Moving it takes effect
 * immediately for every read that follows, including bookings already placed
 * but not yet serviced. There is no vault rule distinguishing "relocation"
 * from "correction"; both are the same write.
 *
 * Same RLS write as `useCreateHub`/`useUpdateHubClockInRadius` — `hubs_admin_write`
 * (0003) already grants admin/superadmin UPDATE on `hubs`, so this needed no
 * new grant, no Edge Function, and no migration.
 */
export function useUpdateHubLocation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { hubId: string; lat: number; lng: number }) => {
      const { error } = await supabase
        .from("hubs")
        .update({ geo: `POINT(${input.lng} ${input.lat})` })
        .eq("id", input.hubId);
      if (error) throw error;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["hubs-list"] });
      queryClient.invalidateQueries({ queryKey: ["hub-detail", variables.hubId] });
    },
  });
}

/**
 * Set a hub's three-letter code — the prefix of its ticket ids (CAI-SOS-0042,
 * 0286). Super Admin only, and the database enforces that (0304): a changed
 * code restarts the hub's ticket count, so it is not an everyday edit.
 */
export function useUpdateHubCode() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { hubId: string; code: string | null }) => {
      const { error } = await supabase
        .from("hubs")
        .update({ code: input.code })
        .eq("id", input.hubId);
      if (error) {
        if (error.code === "23505") throw new Error("Another hub already uses that code.");
        throw error;
      }
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["hubs-list"] });
      queryClient.invalidateQueries({ queryKey: ["hub-detail", variables.hubId] });
    },
  });
}

/**
 * Toggle a hub's two flags on an EXISTING hub.
 *
 * ⚠ `bookable` is the one with consequences. It decides whether client bookings
 * can be served from this hub at all: `selectServingHub` (0056) considers only
 * bookable hubs, on the server AND in the client's surcharge preview. Turning it
 * on makes this hub start receiving real clients; turning it off makes every
 * client who was covered only by it fall out of the service area.
 *
 * `is_pilot` is a LABEL. Nothing operational reads it — not one migration, not
 * one Edge Function — it only renders a badge here. Kept editable because it is
 * the record of where the pilot runs, but changing it serves nobody a booking.
 *
 * They are separate on purpose: a second hub goes live bookable without being
 * the pilot, and the pilot hub could be paused without ceasing to be the pilot.
 */
export function useUpdateHubFlags() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { hubId: string; bookable?: boolean; isPilot?: boolean }) => {
      const patch: { bookable?: boolean; is_pilot?: boolean } = {};
      if (input.bookable !== undefined) patch.bookable = input.bookable;
      if (input.isPilot !== undefined) patch.is_pilot = input.isPilot;
      const { error } = await supabase.from("hubs").update(patch).eq("id", input.hubId);
      if (error) throw error;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["hubs-list"] });
      queryClient.invalidateQueries({ queryKey: ["hub-detail", variables.hubId] });
      queryClient.invalidateQueries({ queryKey: ["hub-portfolio-summary"] });
    },
  });
}
